// Workbook runner: one exercise at a time with a progress bar + live correct
// count, big tappable cards (multiple-choice / fill-blank-choose / inflect-choose
// / identify / illustration-choose / translate / sandhi), an on-screen keyboard
// (type-in), a two-column matcher (match), a tile-ordering tray (order), a
// bucket sorter (categorize), a multi-blank cloze filler (cloze), and a
// multi-axis parser (parse). Immediate manuscript-toned correct/incorrect
// feedback, sticky bottom action bar. All styling via the course UI kit.

import { useMemo, useState } from 'react';
import {
  exerciseWeight, gradeAnswer, gradeMatch, gradeOrder, gradeCategorize, gradeCloze, gradeParse, scorePercent,
} from './engine';
import type {
  GenChoice, GenMatch, GenType, GenOrder, GenCategorize, GenCloze, GenParse, GeneratedExercise, PromptModel,
} from './types';
import { IastKeyboard } from './IastKeyboard';
import { DevanagariKeyboard } from './DevanagariKeyboard';

export function ExerciseRunner({
  exercises,
  onFinish,
}: {
  exercises: GeneratedExercise[];
  onFinish: (r: { correct: number; total: number; score: number }) => void;
}) {
  const totalItems = exercises.length;
  const totalWeight = useMemo(() => exercises.reduce((n, e) => n + exerciseWeight(e), 0), [exercises]);

  const [index, setIndex] = useState(0);
  const [correct, setCorrect] = useState(0);

  // Per-exercise transient state, reset on advance.
  const [selected, setSelected] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [pendingLeft, setPendingLeft] = useState<string | null>(null);
  const [ordered, setOrdered] = useState<string[]>([]);
  const [catMap, setCatMap] = useState<Record<string, string>>({});
  const [pendingItem, setPendingItem] = useState<string | null>(null);
  const [clozeMap, setClozeMap] = useState<Record<string, string>>({});
  const [pendingBlank, setPendingBlank] = useState<string | null>(null);
  const [parseMap, setParseMap] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState(false);
  const [gained, setGained] = useState(0);

  const ex = exercises[index];
  if (!ex) {
    return <div className="px-4 py-10 text-center"><p className="font-grotesk text-[0.9rem] text-[var(--color-ink-mute)]">No exercises to run.</p></div>;
  }

  const canCheck =
    ex.type === 'choice' ? selected !== null
      : ex.type === 'type' ? typed.trim().length > 0
        : ex.type === 'match' ? Object.keys(mapping).length === ex.left.length
          : ex.type === 'order' ? ordered.length === ex.tiles.length
            : ex.type === 'categorize' ? Object.keys(catMap).length === ex.items.length
              : ex.type === 'cloze' ? Object.keys(clozeMap).length === ex.blanks.length
                : ex.type === 'parse' ? Object.keys(parseMap).length === ex.axes.length
                  : false;

  const weight = exerciseWeight(ex);
  const wasCorrect = checked && gained === weight;
  const multiPart = ex.type === 'match' || ex.type === 'categorize' || ex.type === 'cloze' || ex.type === 'parse';

  const handleCheck = () => {
    if (checked || !canCheck) return;
    let g = 0;
    if (ex.type === 'choice') g = gradeAnswer(ex, selected ?? '') ? 1 : 0;
    else if (ex.type === 'type') g = gradeAnswer(ex, typed) ? 1 : 0;
    else if (ex.type === 'match') g = gradeMatch(ex, mapping).correct;
    else if (ex.type === 'order') g = gradeOrder(ex, ordered) ? 1 : 0;
    else if (ex.type === 'categorize') g = gradeCategorize(ex, catMap).correct;
    else if (ex.type === 'cloze') g = gradeCloze(ex, clozeMap).correct;
    else if (ex.type === 'parse') g = gradeParse(ex, parseMap).correct;
    setGained(g);
    setCorrect((c) => c + g);
    setChecked(true);
  };

  const handleContinue = () => {
    const next = index + 1;
    if (next >= totalItems) {
      onFinish({ correct, total: totalWeight, score: scorePercent(correct, totalWeight) });
      return;
    }
    setIndex(next);
    setSelected(null); setTyped(''); setMapping({}); setPendingLeft(null);
    setOrdered([]); setCatMap({}); setPendingItem(null); setClozeMap({}); setPendingBlank(null); setParseMap({});
    setChecked(false); setGained(0);
  };

  const progressPct = Math.round((index / totalItems) * 100);
  const liveFill = ex.type === 'choice' ? selected ?? '' : ex.type === 'type' ? typed : '';
  const showSingleAnswer = !wasCorrect && (ex.type === 'choice' || ex.type === 'type' || ex.type === 'order');
  const singleAnswerDeva =
    (ex.type === 'choice' && ex.optionScript === 'deva') ||
    (ex.type === 'type' && ex.answerScript === 'deva') ||
    (ex.type === 'order' && ex.answerScript === 'deva');

  return (
    <div className="flex flex-col min-h-[60vh]">
      {/* Progress + live correct count */}
      <div className="flex items-center gap-3 mb-7">
        <div className="flex-1 h-2 rounded-full bg-[var(--color-vellum-dim)] overflow-hidden border border-[var(--color-rule)]">
          <div className="h-full bg-[var(--color-violet)] transition-[width] duration-300 ease-out" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="ex-mark is-correct font-grotesk text-[0.72rem] tabular-nums flex items-center gap-1"><span aria-hidden>✓</span>{correct}</span>
        <span className="font-grotesk text-[0.72rem] tabular-nums text-[var(--color-ink-mute)]">{index + 1} / {totalItems}</span>
      </div>

      <div className="flex-1">
        <p className="font-grotesk text-[0.62rem] tracking-[0.2em] uppercase text-[var(--color-ink-mute)] mb-4">{ex.instruction}</p>

        {(ex.type === 'choice' || ex.type === 'type') && (
          <Prompt model={ex.prompt} fill={liveFill} checked={checked} answer={ex.answer} />
        )}

        {ex.type === 'choice' && (
          <Options ex={ex} selected={selected} checked={checked} onSelect={(o) => !checked && setSelected(o)} />
        )}
        {ex.type === 'type' && (
          <TypeArea ex={ex} value={typed} checked={checked}
            onInsert={(s) => !checked && setTyped((t) => t + s)}
            onBackspace={() => !checked && setTyped((t) => t.slice(0, -1))} />
        )}
        {ex.type === 'match' && (
          <Matcher ex={ex} mapping={mapping} pendingLeft={pendingLeft} checked={checked}
            onPickLeft={(id) => !checked && setPendingLeft(id)}
            onPickRight={(rid) => {
              if (checked || !pendingLeft) return;
              setMapping((m) => {
                const next = { ...m };
                for (const k of Object.keys(next)) if (next[k] === rid) delete next[k];
                next[pendingLeft] = rid;
                return next;
              });
              setPendingLeft(null);
            }} />
        )}
        {ex.type === 'order' && (
          <Orderer ex={ex} ordered={ordered} checked={checked} correct={wasCorrect}
            onAppend={(id) => !checked && setOrdered((o) => (o.includes(id) ? o : [...o, id]))}
            onRemove={(id) => !checked && setOrdered((o) => o.filter((x) => x !== id))} />
        )}
        {ex.type === 'categorize' && (
          <Categorizer ex={ex} catMap={catMap} pendingItem={pendingItem} checked={checked}
            onPickItem={(id) => !checked && setPendingItem(id)}
            onPickBucket={(bid) => {
              if (checked || !pendingItem) return;
              setCatMap((m) => ({ ...m, [pendingItem]: bid }));
              setPendingItem(null);
            }}
            onUnplace={(id) => { if (checked) return; setCatMap((m) => { const n = { ...m }; delete n[id]; return n; }); setPendingItem(id); }} />
        )}
        {ex.type === 'cloze' && (
          <ClozeFiller ex={ex} clozeMap={clozeMap} pendingBlank={pendingBlank} checked={checked}
            onPickBlank={(bid) => { if (checked) return; setClozeMap((m) => { const n = { ...m }; delete n[bid]; return n; }); setPendingBlank(bid); }}
            onPickChip={(cid) => {
              if (checked || !pendingBlank) return;
              setClozeMap((m) => {
                const next = { ...m };
                for (const k of Object.keys(next)) if (next[k] === cid) delete next[k];
                next[pendingBlank] = cid;
                return next;
              });
              setPendingBlank(null);
            }} />
        )}
        {ex.type === 'parse' && (
          <Parser ex={ex} parseMap={parseMap} checked={checked}
            onPick={(axis, val) => !checked && setParseMap((m) => ({ ...m, [axis]: val }))} />
        )}

        {checked && (
          <div className={`ex-feedback ${wasCorrect ? 'is-correct' : 'is-wrong'}`}>
            <p className="ex-feedback-label mb-1.5">
              {multiPart ? `${gained} / ${weight} correct` : wasCorrect ? 'Correct ✓' : 'Not quite'}
            </p>
            {showSingleAnswer && (
              <p className="text-[0.95rem] text-[var(--color-ink)] mb-1">
                Answer:{' '}
                <span className={singleAnswerDeva ? 'font-deva text-[1.4rem] align-middle' : 'font-semibold'}>{ex.answer}</span>
              </p>
            )}
            {ex.feedback && <p className="text-[0.9rem] text-[var(--color-ink-soft)]">{ex.feedback}</p>}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 -mx-4 sm:mx-0 mt-8 px-4 sm:px-0 pt-4 bg-[var(--color-vellum)] border-t border-[var(--color-rule)]" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        {!checked ? (
          <button type="button" onClick={handleCheck} disabled={!canCheck} className="course-cta w-full sm:w-auto sm:min-w-[200px]">Check</button>
        ) : (
          <button type="button" onClick={handleContinue} className={`course-cta w-full sm:w-auto sm:min-w-[200px] ${wasCorrect ? 'is-done' : ''}`}>{index + 1 >= totalItems ? 'Finish' : 'Continue'}</button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Prompt({ model, fill, checked, answer }: { model: PromptModel; fill: string; checked: boolean; answer: string }) {
  if (model.kind === 'token') {
    const big = model.script === 'deva' ? 'font-deva text-[clamp(2.2rem,8vw,3.6rem)]' : model.script === 'label' ? 'text-[clamp(1.3rem,4.5vw,2rem)] font-medium' : 'text-[clamp(1.6rem,5.5vw,2.6rem)]';
    return (
      <div className="ex-prompt mb-7">
        <span className={`${big} leading-tight text-center text-[var(--color-ink)] px-3`}>{model.text}</span>
        {model.sub && <span className="text-[1rem] text-[var(--color-ink-mute)]">{model.sub}</span>}
      </div>
    );
  }
  if (model.kind === 'sentence') {
    const fontDeva = model.script === 'deva';
    const shown = checked ? answer : fill;
    return (
      <div className="ex-prompt mb-7">
        <p className={`${fontDeva ? 'font-deva' : ''} text-[clamp(1.4rem,5vw,2rem)] leading-relaxed text-center text-[var(--color-ink)] px-3`}>
          {model.segments.map((s, i) =>
            s.role === 'blank' ? (
              <span key={i} className={`inline-block min-w-[3.5ch] mx-1 px-1 text-center border-b-2 ${shown ? 'border-[var(--color-violet)] text-[var(--color-violet)]' : 'border-[var(--color-rule-strong)] text-[var(--color-ink-light)]'}`}>{shown || '  '}</span>
            ) : s.role === 'mark' ? (
              <span key={i} className="px-1.5 rounded-sm bg-[var(--color-vellum-warm)] text-[var(--color-violet)] font-semibold">{s.text}</span>
            ) : (
              <span key={i}>{s.text}</span>
            ),
          )}
        </p>
        {model.cue && <span className="font-grotesk text-[0.82rem] text-[var(--color-ink-mute)] mt-1">{model.cue}</span>}
      </div>
    );
  }
  // illustration
  return (
    <div className="mb-7">
      <div className="ex-prompt overflow-hidden p-0" dangerouslySetInnerHTML={{ __html: model.svg }} />
      <p className="font-grotesk text-[0.95rem] text-[var(--color-ink-soft)] text-center mt-3">{model.instruction}</p>
    </div>
  );
}

function Options({ ex, selected, checked, onSelect }: { ex: GenChoice; selected: string | null; checked: boolean; onSelect: (o: string) => void }) {
  const deva = ex.optionScript === 'deva';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {ex.options.map((opt) => {
        const chosen = selected === opt;
        const isAnswer = opt === ex.answer;
        let cls = '';
        if (checked) cls = isAnswer ? 'is-correct' : chosen ? 'is-wrong' : 'is-muted';
        else if (chosen) cls = 'is-selected';
        return (
          <button key={opt} type="button" disabled={checked} onClick={() => onSelect(opt)} className={`ex-option ${cls}`}>
            <span className={deva ? 'font-deva text-[1.9rem] leading-none' : ex.optionScript === 'label' ? 'text-[1.05rem] font-medium leading-snug' : 'text-[1.25rem] leading-none'}>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

function TypeArea({ ex, value, checked, onInsert, onBackspace }: { ex: GenType; value: string; checked: boolean; onInsert: (s: string) => void; onBackspace: () => void }) {
  const deva = ex.answerScript === 'deva';
  const empty = value.length === 0;
  return (
    <div className="flex flex-col gap-5">
      <div className={`min-h-[64px] px-4 py-3 grid place-items-center rounded border ${empty ? 'border-dashed border-[var(--color-rule)]' : 'border-[var(--color-violet)]'} bg-[var(--color-vellum-dim)]`} aria-live="polite">
        {empty ? <span className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">Tap the keys to build your answer</span>
          : <span className={`${deva ? 'font-deva' : ''} text-[1.7rem] leading-none text-[var(--color-ink)] break-all text-center`}>{value}</span>}
      </div>
      {!checked && ex.keyboard !== 'none' && (
        <div className="max-h-[44vh] overflow-y-auto -mx-1 px-1">
          {ex.keyboard === 'iast' ? <IastKeyboard onInsert={onInsert} onBackspace={onBackspace} /> : <DevanagariKeyboard onInsert={onInsert} onBackspace={onBackspace} />}
        </div>
      )}
    </div>
  );
}

function Matcher({ ex, mapping, pendingLeft, checked, onPickLeft, onPickRight }: {
  ex: GenMatch; mapping: Record<string, string>; pendingLeft: string | null; checked: boolean;
  onPickLeft: (id: string) => void; onPickRight: (id: string) => void;
}) {
  const leftNum = new Map(ex.left.map((l, i) => [l.id, i + 1]));
  const rightToLeft: Record<string, string> = {};
  for (const [lid, rid] of Object.entries(mapping)) rightToLeft[rid] = lid;
  const cell = (item: { id: string; text: string; script: string }, side: 'l' | 'r') => {
    const num = side === 'l' ? (mapping[item.id] ? leftNum.get(item.id) : null) : (rightToLeft[item.id] ? leftNum.get(rightToLeft[item.id]!) : null);
    let cls = '';
    if (checked) {
      const lid = side === 'l' ? item.id : rightToLeft[item.id];
      const ok = lid != null && mapping[lid] === ex.correct[lid];
      cls = num != null ? (ok ? 'is-correct' : 'is-wrong') : 'is-muted';
    } else if ((side === 'l' && pendingLeft === item.id) || num != null) cls = 'is-selected';
    const deva = item.script === 'deva';
    return (
      <button key={item.id} type="button" disabled={checked} onClick={() => (side === 'l' ? onPickLeft(item.id) : onPickRight(item.id))} className={`ex-option ${cls} !min-h-[52px] flex items-center justify-between gap-2`}>
        <span className={deva ? 'font-deva text-[1.5rem]' : item.script === 'label' ? 'text-[0.98rem]' : 'text-[1.15rem]'}>{item.text}</span>
        {num != null && <span className="font-grotesk text-[0.7rem] font-bold text-[var(--color-violet)] tabular-nums">{num}</span>}
      </button>
    );
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-2.5">{ex.left.map((l) => cell(l, 'l'))}</div>
      <div className="flex flex-col gap-2.5">{ex.right.map((r) => cell(r, 'r'))}</div>
    </div>
  );
}

function Orderer({ ex, ordered, checked, correct, onAppend, onRemove }: {
  ex: GenOrder; ordered: string[]; checked: boolean; correct: boolean;
  onAppend: (id: string) => void; onRemove: (id: string) => void;
}) {
  const byId = new Map(ex.tiles.map((t) => [t.id, t]));
  const deva = ex.answerScript === 'deva';
  const tileCls = deva ? 'font-deva text-[1.7rem] leading-none' : 'text-[1.2rem] leading-none';
  return (
    <div className="flex flex-col gap-5">
      {/* Answer row — the sequence being built */}
      <div className={`min-h-[64px] px-3 py-3 flex flex-wrap items-center justify-center gap-2 rounded border ${ordered.length ? (checked ? (correct ? 'border-[var(--color-correct)] bg-[var(--color-correct-bg)]' : 'border-[var(--color-wrong)] bg-[var(--color-wrong-bg)]') : 'border-[var(--color-violet)]') : 'border-dashed border-[var(--color-rule)]'} bg-[var(--color-vellum-dim)]`} aria-live="polite">
        {ordered.length === 0 && <span className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">Tap the tiles in order</span>}
        {ordered.map((id) => {
          const t = byId.get(id);
          if (!t) return null;
          return (
            <button key={id} type="button" disabled={checked} onClick={() => onRemove(id)} className="ex-tile is-placed">
              <span className={tileCls}>{t.text}</span>
            </button>
          );
        })}
      </div>
      {/* Tray — unused tiles */}
      {!checked && (
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          {ex.tiles.filter((t) => !ordered.includes(t.id)).map((t) => (
            <button key={t.id} type="button" onClick={() => onAppend(t.id)} className="ex-tile">
              <span className={tileCls}>{t.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Categorizer({ ex, catMap, pendingItem, checked, onPickItem, onPickBucket, onUnplace }: {
  ex: GenCategorize; catMap: Record<string, string>; pendingItem: string | null; checked: boolean;
  onPickItem: (id: string) => void; onPickBucket: (bid: string) => void; onUnplace: (id: string) => void;
}) {
  const unplaced = ex.items.filter((it) => !(it.id in catMap));
  const chip = (it: { id: string; text: string; script: string; bucketId: string }, placed: boolean) => {
    const deva = it.script === 'deva';
    let cls = '';
    if (checked && placed) cls = catMap[it.id] === it.bucketId ? 'is-correct' : 'is-wrong';
    else if (!placed && pendingItem === it.id) cls = 'is-selected';
    return (
      <button key={it.id} type="button" disabled={checked}
        onClick={() => (placed ? onUnplace(it.id) : onPickItem(it.id))}
        className={`ex-tile ${cls}`}>
        <span className={deva ? 'font-deva text-[1.5rem] leading-none' : 'text-[1.05rem] leading-none'}>{it.text}</span>
      </button>
    );
  };
  return (
    <div className="flex flex-col gap-4">
      {/* Tray of items to sort */}
      <div className={`min-h-[56px] px-3 py-3 flex flex-wrap items-center justify-center gap-2.5 rounded border border-dashed ${unplaced.length ? 'border-[var(--color-rule)]' : 'border-transparent'} bg-[var(--color-vellum-dim)]`}>
        {unplaced.length === 0 ? <span className="font-grotesk text-[0.8rem] text-[var(--color-ink-mute)]">All sorted — press Check</span> : unplaced.map((it) => chip(it, false))}
      </div>
      {/* Buckets */}
      <div className={`grid gap-3 ${ex.buckets.length >= 4 ? 'grid-cols-2 sm:grid-cols-3' : ex.buckets.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {ex.buckets.map((b) => {
          const inB = ex.items.filter((it) => catMap[it.id] === b.id);
          const armed = pendingItem != null && !checked;
          return (
            <button key={b.id} type="button" disabled={checked || !armed}
              onClick={() => onPickBucket(b.id)}
              className={`text-left rounded border p-2.5 min-h-[88px] transition-colors ${armed ? 'border-[var(--color-violet)] bg-[var(--color-vellum-warm)] cursor-pointer' : 'border-[var(--color-rule-strong)] bg-[var(--color-vellum)]'}`}>
              <span className="block font-grotesk text-[0.66rem] tracking-[0.12em] uppercase text-[var(--color-ink-mute)] mb-1.5">{b.label}</span>
              <span className="flex flex-wrap gap-1.5">{inB.map((it) => chip(it, true))}</span>
            </button>
          );
        })}
      </div>
      {checked && <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-mute)] text-center">Green = sorted correctly. Tap-to-sort: pick an item, then tap its category.</p>}
    </div>
  );
}

function ClozeFiller({ ex, clozeMap, pendingBlank, checked, onPickBlank, onPickChip }: {
  ex: GenCloze; clozeMap: Record<string, string>; pendingBlank: string | null; checked: boolean;
  onPickBlank: (bid: string) => void; onPickChip: (cid: string) => void;
}) {
  const deva = ex.script === 'deva';
  const chipById = new Map(ex.bank.map((c) => [c.id, c]));
  const blankNum = new Map(ex.blanks.map((b, i) => [b.id, i + 1]));
  const usedChips = new Set(Object.values(clozeMap));
  return (
    <div className="flex flex-col gap-5">
      {/* Sentence with blank slots */}
      <div className="ex-prompt">
        <p className={`${deva ? 'font-deva' : ''} text-[clamp(1.3rem,4.6vw,1.9rem)] leading-relaxed text-center text-[var(--color-ink)] px-2`}>
          {ex.segments.map((s, i) => {
            if (s.role !== 'blank' || !s.blankId) return <span key={i}>{s.text}</span>;
            const bid = s.blankId;
            const cid = clozeMap[bid];
            const filled = cid ? chipById.get(cid) : undefined;
            let cls = 'border-[var(--color-rule-strong)] text-[var(--color-ink-light)]';
            if (checked) cls = cid === ex.correct[bid] ? 'border-[var(--color-correct)] text-[var(--color-correct)]' : 'border-[var(--color-wrong)] text-[var(--color-wrong)]';
            else if (pendingBlank === bid) cls = 'border-[var(--color-violet)] bg-[var(--color-vellum-warm)] text-[var(--color-violet)]';
            else if (filled) cls = 'border-[var(--color-violet)] text-[var(--color-violet)]';
            return (
              <button key={i} type="button" disabled={checked} onClick={() => onPickBlank(bid)}
                className={`inline-flex items-baseline gap-1 mx-1 px-2 min-w-[3.5ch] text-center border-b-2 align-baseline ${cls}`}>
                <span className="font-grotesk not-italic text-[0.6rem] tabular-nums opacity-70">{blankNum.get(bid)}</span>
                <span>{filled ? filled.text : '  '}</span>
              </button>
            );
          })}
        </p>
      </div>
      {/* Cues, numbered to the blanks */}
      <ol className="flex flex-col gap-1">
        {ex.blanks.map((b, i) => (
          <li key={b.id} className="font-grotesk text-[0.82rem] text-[var(--color-ink-mute)]"><span className="tabular-nums font-semibold text-[var(--color-ink-soft)]">{i + 1}.</span> {b.cue}</li>
        ))}
      </ol>
      {/* Word bank */}
      {!checked && (
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          {ex.bank.map((c) => (
            <button key={c.id} type="button" disabled={usedChips.has(c.id)} onClick={() => onPickChip(c.id)}
              className={`ex-tile ${usedChips.has(c.id) ? 'is-muted' : ''}`}>
              <span className={deva ? 'font-deva text-[1.5rem] leading-none' : 'text-[1.1rem] leading-none'}>{c.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Parser({ ex, parseMap, checked, onPick }: {
  ex: GenParse; parseMap: Record<string, string>; checked: boolean;
  onPick: (axis: string, val: string) => void;
}) {
  const deva = ex.script === 'deva';
  return (
    <div className="flex flex-col gap-5">
      {/* The marked sentence */}
      <div className="ex-prompt">
        <p className={`${deva ? 'font-deva' : ''} text-[clamp(1.4rem,5vw,2rem)] leading-relaxed text-center text-[var(--color-ink)] px-2`}>
          {ex.segments.map((s, i) =>
            s.role === 'mark'
              ? <span key={i} className="px-1.5 rounded-sm bg-[var(--color-vellum-warm)] text-[var(--color-violet)] font-semibold">{s.text}</span>
              : <span key={i}>{s.text}</span>,
          )}
        </p>
      </div>
      {/* One single-select row per axis */}
      {ex.axes.map((ax) => (
        <div key={ax.key} className="flex flex-col gap-2">
          <span className="font-grotesk text-[0.66rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)]">{ax.label}</span>
          <div className="flex flex-wrap gap-2">
            {ax.options.map((opt) => {
              const chosen = parseMap[ax.key] === opt;
              const isAnswer = opt === ax.answer;
              let cls = '';
              if (checked) cls = isAnswer ? 'is-correct' : chosen ? 'is-wrong' : 'is-muted';
              else if (chosen) cls = 'is-selected';
              const optDeva = deva && /[ऀ-ॿ]/.test(opt);
              return (
                <button key={opt} type="button" disabled={checked} onClick={() => onPick(ax.key, opt)} className={`ex-tile ${cls}`}>
                  <span className={optDeva ? 'font-deva text-[1.3rem] leading-none' : 'text-[0.98rem] leading-snug'}>{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
